import React from "react";
import { Box, Divider, Stack, Typography } from "@mui/material";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import { useIntl } from "react-intl";
import cmmsMessages from "../cmmsMessages";
import { formatTimestamp } from "../cmmsFormat";
import { buildToolServiceEntrySummary } from "../toolServiceStatus";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton } from "./CmmsStates";

/*
 * When a tool was serviced, newest first.
 *
 * Presentational only, like ToolServiceStatusCell next door: it takes the entries it is given
 * and never fetches. That is deliberate rather than tidy-minded. The panel is mounted from
 * /tools, which is route-gated to [1, 2] — so the technician who performed the service, and
 * who has the best reason to check what was booked, cannot reach it. A second mount point on a
 * page they can reach is expected, and this shape makes it cost a wrapper rather than a rewrite.
 *
 * `userNameById` comes from the caller for the same reason the work order panel takes it: the
 * user list is one request per page, not one per panel.
 */
export default function ToolServiceHistoryPanel({
  entries,
  isLoading = false,
  isError = false,
  error,
  onRetry,
  isRetrying = false,
  userNameById,
}) {
  const intl = useIntl();

  if (isLoading) {
    return <CmmsListSkeleton rows={3} height={44} />;
  }

  if (isError) {
    return (
      <CmmsErrorState
        error={error}
        fallback={intl.formatMessage(cmmsMessages.toolHistoryLoadFailed)}
        onRetry={onRetry}
        isRetrying={isRetrying}
      />
    );
  }

  const rows = entries ?? [];

  /*
   * "Nothing serviced" is an answer, not a failure — most tools in a 1 465-row register have
   * never been through a work order. The hint says where entries come from, because a user
   * looking at an empty list has no way to tell whether the feature is broken or simply unused.
   */
  if (rows.length === 0) {
    return (
      <CmmsEmptyState
        icon={<BuildCircleIcon />}
        title={intl.formatMessage(cmmsMessages.toolHistoryEmpty)}
        hint={intl.formatMessage(cmmsMessages.toolHistoryEmptyHint)}
      />
    );
  }

  return (
    <Stack divider={<Divider flexItem />} spacing={0}>
      {rows.map((entry) => (
        <Box key={entry.id} sx={{ py: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {formatTimestamp(intl, entry.servicedAt)}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
            {buildToolServiceEntrySummary(entry, intl, userNameById?.get(entry.servicedByUserId))}
          </Typography>
          {entry.notes &&
            <Typography variant="body2" sx={{ mt: 0.75, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {entry.notes}
            </Typography>
          }
        </Box>
      ))}
    </Stack>
  );
}
